import {
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChildren,
    QueryList,
    Output,
    EventEmitter,
    AfterViewInit
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { UsersService } from '../../../../Core/Users/services/users.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ImageLightBoxComponent } from '../../../../Common/Dialogs/components/ImageLightBox/image-light-box.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { BriefingListItemModel } from '../../../../Common/MonitorShared/model/briefing-list-item.model';
import { AuditableModel } from '../../../../Common/Misc/models/auditable.model';
import { FilterListProviderServiceInterface } from '../../../../Common/Filter/services/filter-list-provider-service.interface';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { FilterTypeProviderServiceInterface } from '../../../../Common/MonitorShared/misc/filter-type-provider-service.interface';
import { UserAppMonitorPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import {
    MonitoringBriefingsDisplayFilterModel,
    MonitoringCoverageAlertsDisplayFilterModel,
    MonitoringSavedCoverageDisplayFilterModel,
    MonitoringSubscriptionsDisplayFilterModel,
    MonitoringTopicsDisplayFilterModel
} from '../../models/monitoring-display-filter.model';
import {
    MonitoringDisplaySettingsModel,
    MonitoringSavedCoverageDisplaySettingsModel,
    MonitoringBriefingDisplaySettingsModel,
    MonitoringSubscriptionDisplaySettingsModel} from '../../models/display-settings.model';
import { FilterTypeCheckboxModel } from '../../../../Common/Filter/models/filter-type-checkbox.model';
import { BriefingStatusEnum } from '../../../../Common/MonitorShared/enum/briefing-status.enum';
import { MediaSubscriptionMediaTypeEnum } from '../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import type { SectionListItems } from '../UserPreferences/user-preferences.component';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ManageTemplateDialogComponent } from '../../../../Common/MonitorShared/components/ManageTemplateDialog/manage-template-dialog.component';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FolderService } from '@Monitor/Core/services/folder.service';
import { ListEntitiesVisibilityEnum } from '../../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { FilterTypeRadioModel } from '../../../../Common/Filter/models/filter-type-radio.model';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { AlertStatusEnum } from '../../../../Common/MonitorShared/enum/alert-type.enum';


enum BriefingTemplateOptions {
    one = 'BRIEFING_1',
    two = 'BRIEFING_2',
    three = 'BRIEFING_3',
    saved = 'saved'
}


@Component({
    selector: 'agpr-monitor-user-preferences',
    templateUrl: './monitor-user-preferences.component.html',
    styleUrls: [
        './monitor-user-preferences.component.scss',
        '../UserPreferences/user-preferences.component.scss'
    ],
    standalone: false
})
export class MonitorUserPreferencesComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() monitoringPreferences: UserAppMonitorPreferencesModel;

    @Output() checkDirty = new EventEmitter<void>();
    @Output() sectionListItems = new EventEmitter<SectionListItems>();


    @ViewChildren('section') set sections(list: QueryList<ElementRef>) {
        if (list) {
            this.sectionListItems.emit(
                list.toArray().map(item => ({
                    label: `UserPreferences.${item.nativeElement.id}`,
                    value: item
                }))
            );
        }
    }

    permissions: userPermissionsEnum[] = [];

    defaultTopicsFilters: MonitoringTopicsDisplayFilterModel;
    defaultSavedCoverageFilters: MonitoringSavedCoverageDisplayFilterModel;
    defaultBriefingsFilters: MonitoringBriefingsDisplayFilterModel;
    defaultAlertsFilters: MonitoringCoverageAlertsDisplayFilterModel;
    defaultSubscriptionsFilters: MonitoringSubscriptionsDisplayFilterModel;

    topicsDisplaySettings: MonitoringDisplaySettingsModel;
    savedCoverageDisplaySettings: MonitoringSavedCoverageDisplaySettingsModel;
    briefingDisplaySettings: MonitoringBriefingDisplaySettingsModel;
    subscriptionDisplaySettings: MonitoringSubscriptionDisplaySettingsModel;

    topics: AuditableModel[] = [];
    mediaSubscriptions: AuditableModel[] = [];
    folders: AuditableModel[] = [];
    briefings: AuditableModel[] = [];
    alerts: AuditableModel[] = [];


    selectedBriefingTemplateDropdown;
    briefingTemplateListItems: DropdownOptionModel[] = [];
    briefingTemplateSelection: BriefingTemplateOptions = BriefingTemplateOptions.one;
    hasSubscriptionPermissions = false;

    private afterView = false;
    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly resultsViewModeEnum = MonitoringViewModesEnum;
    readonly defaultBriefingTemplateOptions = BriefingTemplateOptions;

    constructor(
        public journodexRegionsService: JournodexRegionsService,
        public localizationService: LocalizationService,
        protected usersService: UsersService,
        public coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        @Inject('MonitoringLeftSidebarService') public monitoringLeftSidebarService: FilterTypeProviderServiceInterface,
        @Inject('MediaSubscriptionService') public mediaSubscriptionsService: FilterListProviderServiceInterface,
        @Inject('FolderService') public folderService: FolderService,
        @Inject('BriefingsService') public briefingsService: FilterListProviderServiceInterface,
        @Inject('BriefingTemplateService') public briefingTemplateService: FilterListProviderServiceInterface,
        @Inject('AlertsService') public alertsService: FilterListProviderServiceInterface,
        @Inject('TopicsService') public topicsService: FilterListProviderServiceInterface,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x),
                tap(permissions => {
                    this.permissions = permissions;
                    this.updateSortTypeOptions();
                }),
                map(permissions => {
                    this.hasSubscriptionPermissions = permissions.includes(userPermissionsEnum.mediaSubscriptions);
                    return this.hasSubscriptionPermissions;
                }),
                filter(hasSubscriptions => !!hasSubscriptions),
                switchMap(() => this.mediaSubscriptionsService.getFilterListItems()),
                filter(x => !!x),
            ).subscribe(mediaSubscriptions => {
                this.mediaSubscriptions = mediaSubscriptions;
                this.defaultSubscriptionsFilters = new MonitoringSubscriptionsDisplayFilterModel(this.monitoringPreferences.subscriptionsPrefs.defaultFilters);
                const [_, statusFilter, typeFilter] = this.defaultSubscriptionsFilters.getAllFilters();
                this.monitoringLeftSidebarService.getEnumStringOptions(
                    (statusFilter as FilterTypeCheckboxModel<string>),
                    mediaSubscriptions,
                    (item: MediaSubscription) => item.status,
                    {
                        prefix: 'MediaSubscriptions.status.',
                        enumType: MediaSubscriptionStatusEnum
                    }
                );
                this.monitoringLeftSidebarService.getEnumStringOptions(
                    (typeFilter as FilterTypeCheckboxModel<string>),
                    mediaSubscriptions,
                    (item: MediaSubscription) => item.type,
                    {
                        prefix: 'MediaSubscriptionType.',
                        enumType: MediaSubscriptionMediaTypeEnum
                    }
                );
            }),
            this.folderService.all$.pipe(
                filter(x => !!x)
            ).subscribe(folders => {
                this.folders = folders;
                this.defaultSavedCoverageFilters = new MonitoringSavedCoverageDisplayFilterModel(this.monitoringPreferences.savedCoveragePrefs.defaultFilters);
            }),
            this.topicsService.getFilterListItems().pipe(
                filter(x => !!x)
            ).subscribe(topics => {
                this.topics = topics;
                this.defaultTopicsFilters = new MonitoringTopicsDisplayFilterModel(this.monitoringPreferences.topicsPrefs.defaultFilters);
                const [, autoTaggingFilter] = this.defaultTopicsFilters.getAllFilters();
                this.monitoringLeftSidebarService.getTagFilterOptions(
                    (autoTaggingFilter as FilterTypeCheckboxModel<number>),
                    this.topics, this.monitoringLeftSidebarService.getAutoTagIdsFromTopic
                );
                const [, , autoSavingFilter] = this.defaultTopicsFilters.getAllFilters();
                this.monitoringLeftSidebarService.getAutoSavingFilterOptions( autoSavingFilter as FilterTypeRadioModel<ListEntitiesVisibilityEnum>);
            }),
            this.briefingsService.getFilterListItems().pipe(
                filter(x => !!x)
            ).subscribe(briefingListItems => {
                this.briefings = briefingListItems;
                this.defaultBriefingsFilters = new MonitoringBriefingsDisplayFilterModel(this.monitoringPreferences.briefingsPrefs.defaultFilters);
                const [_, statusFilter] = this.defaultBriefingsFilters.getAllFilters();
                this.monitoringLeftSidebarService.getEnumStringOptions(
                    (statusFilter as FilterTypeCheckboxModel<string>),
                    briefingListItems, (item: BriefingListItemModel) => item.status,
                    {
                        prefix: 'Briefings.status.',
                        enumType: BriefingStatusEnum
                    }
                );
            }),
            this.alertsService.getFilterListItems().pipe(
                filter(x => !!x)
            ).subscribe(alerts => {
                this.alerts = alerts;
                this.defaultAlertsFilters = new MonitoringCoverageAlertsDisplayFilterModel(this.monitoringPreferences.coverageAlertsPrefs.defaultFilters);
                const [_, statusFilter] = this.defaultAlertsFilters.getAllFilters();
                this.monitoringLeftSidebarService.getEnumStringOptions(
                    (statusFilter as FilterTypeCheckboxModel<string>),
                    alerts, (item: BriefingListItemModel) => item.status,
                    {
                        prefix: 'Alerts.status.',
                        enumType: AlertStatusEnum
                    }
                );
            }),
            this.briefingTemplateService.getFilterListItems().subscribe(
                briefingListItems => {
                    this.briefingTemplateListItems = [];
                    if (!(briefingListItems == null)) {

                        let foundBriefingTemplate = false;
                        const defaultBriefingTemplate = this.monitoringPreferences.briefingsPrefs.defaultBriefingTemplate;
                        if (defaultBriefingTemplate === this.defaultBriefingTemplateOptions.one
                            || defaultBriefingTemplate === this.defaultBriefingTemplateOptions.two
                            || defaultBriefingTemplate === this.defaultBriefingTemplateOptions.three
                        ) {
                            foundBriefingTemplate = true;
                            this.briefingTemplateSelection = defaultBriefingTemplate;
                        }

                        briefingListItems.forEach(item => {
                            const option = new DropdownOptionModel(item.name, item);

                            this.briefingTemplateListItems.push(option);

                            if (!foundBriefingTemplate && defaultBriefingTemplate === (item as BriefingListItemModel).id.toString()) {
                                foundBriefingTemplate = true;
                                this.briefingTemplateSelection = BriefingTemplateOptions.saved;
                                this.selectedBriefingTemplateDropdown = option;
                            }
                        });
                    }
                }
            )
        );
        if (!this.monitoringPreferences.topicsPrefs.defaultDisplaySettings?.resultsViewMode) {
            this.monitoringPreferences.topicsPrefs.defaultDisplaySettings.resultsViewMode = MonitoringViewModesEnum.MENTION;
        }
        this.topicsDisplaySettings = new MonitoringDisplaySettingsModel(this.monitoringPreferences.topicsPrefs.defaultDisplaySettings);

        this.savedCoverageDisplaySettings = new MonitoringSavedCoverageDisplaySettingsModel(
            this.monitoringPreferences.savedCoveragePrefs.defaultDisplaySettings
        );

        this.updateSortTypeOptions();

        this.briefingDisplaySettings = new MonitoringBriefingDisplaySettingsModel(this.monitoringPreferences.briefingsPrefs.defaultDisplaySettings);

        if (!this.monitoringPreferences.subscriptionsPrefs.defaultDisplaySettings?.resultsViewMode) {
            this.monitoringPreferences.subscriptionsPrefs.defaultDisplaySettings.resultsViewMode = MonitoringViewModesEnum.MENTION;
        }
        this.subscriptionDisplaySettings = new MonitoringSubscriptionDisplaySettingsModel(this.monitoringPreferences.subscriptionsPrefs.defaultDisplaySettings);
    }

    ngAfterViewInit(): void {
        this.afterView = true;
    }

    keys (obj: Object): string[]{
        return Object.keys(obj);
    }

    preview(previewImageSrc: string): void {
        this.coreDialogService.open(
            ImageLightBoxComponent,
            {
                inputs: {
                    id: 'briefing-template-preview',
                    src: previewImageSrc,
                    width: 225
                }
            }
        );
    }

    manageTemplatesButtonClicked(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageTemplateDialogComponent, io);
    }

    getUpdatedValues(): UserAppMonitorPreferencesModel {
        return {
            topicsPrefs: {
                defaultFilters: this.defaultTopicsFilters?.getFilterValues() ?? this.monitoringPreferences.topicsPrefs.defaultFilters,
                defaultDisplaySettings: this.topicsDisplaySettings.getCurrentSelections(),
            },
            savedCoveragePrefs: {
                defaultFilters: this.defaultSavedCoverageFilters?.getFilterValues() ?? this.monitoringPreferences.savedCoveragePrefs.defaultFilters,
                defaultDisplaySettings: this.savedCoverageDisplaySettings.getCurrentSelections(),
            },
            briefingsPrefs: {
                defaultFilters: this.defaultBriefingsFilters?.getFilterValues() ?? this.monitoringPreferences.briefingsPrefs.defaultFilters,
                defaultBriefingTemplate: this.determineBriefingTemplate(),
                defaultDisplaySettings: this.briefingDisplaySettings.getCurrentSelections(),
            },
            coverageAlertsPrefs: {
                defaultFilters: this.defaultAlertsFilters?.getFilterValues() ?? this.monitoringPreferences.coverageAlertsPrefs.defaultFilters,
            },
            subscriptionsPrefs: {
                defaultFilters: this.defaultSubscriptionsFilters?.getFilterValues() ?? this.monitoringPreferences.subscriptionsPrefs.defaultFilters,
                defaultDisplaySettings: this.subscriptionDisplaySettings.getCurrentSelections(),
            },
            defaultExportColumns: this.monitoringPreferences.defaultExportColumns
        };
    }

    updateDirtyFlag() {
        if (!this.afterView) {
            return;
        }
        this.checkDirty.emit();
    }

    private determineBriefingTemplate(): string {
        if (this.briefingTemplateSelection !== BriefingTemplateOptions.saved) {
            return this.briefingTemplateSelection;
        } else if (this.selectedBriefingTemplateDropdown?.value) {
            return this.selectedBriefingTemplateDropdown.value.id.toString();
        } else {
            // Saved Template option was clicked, but no option was selected, revert to default
            return this.monitoringPreferences.briefingsPrefs.defaultBriefingTemplate;
        }
    }

    private updateSortTypeOptions() {
        const hasSocialIntelligencePermission: boolean = this.permissions.includes(userPermissionsEnum.socialIntelligence);
        this.topicsDisplaySettings?.updateSortTypeOptions(hasSocialIntelligencePermission);
        this.savedCoverageDisplaySettings?.updateSortTypeOptions(hasSocialIntelligencePermission);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
